import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Lock, Bell, Globe, Save, CheckCircle, X, Mail } from 'lucide-react';
import API from '../api';

const Settings = () => {
    const { t } = useTranslation();
    const [profile, setProfile] = useState({ first_name: '', last_name: '', email: '' });
    const [passwordForm, setPasswordForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
    const [userPrefs, setUserPrefs] = useState({ organizations: [], customers: [] });
    const [loading, setLoading] = useState(true);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);
    const [showPasswordModal, setShowPasswordModal] = useState(false);

    useEffect(() => {
        fetchProfile();
        fetchNotificationPreferences();
    }, []);

    const fetchNotificationPreferences = async () => {
        try {
            const res = await API.get('/me/notification-preferences/');
            setUserPrefs(res.data);
        } catch (err) {
            console.error("Error fetching notification preferences", err);
        }
    };

    const handlePrefToggle = async (type, membershipId, field, currentValue) => {
        try {
            const newValue = !currentValue;
            await API.post('/me/notification-preferences/', {
                type,
                membership_id: membershipId,
                [field]: newValue
            });
            
            setUserPrefs(prev => {
                const listName = type === 'organization' ? 'organizations' : 'customers';
                const updatedList = prev[listName].map(item => {
                    if (item.membership_id === membershipId) {
                        return { ...item, [field]: newValue };
                    }
                    return item;
                });
                return { ...prev, [listName]: updatedList };
            });
            
            setSuccess(t('settings.success_notifications_saved', 'Bildirim tercihleri kaydedildi.'));
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(t('settings.error_notifications_saved', 'Bildirim tercihleri kaydedilemedi.'));
            setTimeout(() => setError(null), 3000);
        }
    };

    const fetchProfile = async () => {
        try {
            const res = await API.get('/me/');
            setProfile({
                first_name: res.data.first_name || '',
                last_name: res.data.last_name || '',
                email: res.data.email || ''
            });
            setLoading(false);
        } catch (err) {
            // Fallback if /me/ endpoint doesn't exist
            setLoading(false);
        }
    };

    const handleProfileSave = async () => {
        try {
            await API.patch('/me/', profile);
            setSuccess(t('settings.success_profile_updated'));
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(t('settings.error_profile_updated'));
            setTimeout(() => setError(null), 3000);
        }
    };

    const handlePasswordChange = async () => {
        if (passwordForm.new_password !== passwordForm.confirm_password) {
            setError(t('settings.error_passwords_mismatch'));
            return;
        }
        try {
            await API.post('/change-password/', {
                old_password: passwordForm.old_password,
                new_password: passwordForm.new_password
            });
            setSuccess(t('settings.success_password_changed'));
            setShowPasswordModal(false);
            setPasswordForm({ old_password: '', new_password: '', confirm_password: '' });
        } catch (err) {
            setError(t('settings.error_password_changed'));
        }
    };

    const requestMagicLink = async () => {
        try {
            await API.post('/auth/magic-link/request/', { email: profile.email });
            setSuccess(t('settings.success_magic_link_sent'));
        } catch (err) {
            setError(t('settings.error_magic_link_sent'));
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>;

    return (
        <div className="max-w-4xl">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('settings.title')}</h1>

            {success && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between text-green-700">
                    <div className="flex items-center"><CheckCircle size={20} className="mr-2" />{success}</div>
                    <button onClick={() => setSuccess(null)}><X size={18} /></button>
                </div>
            )}

            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between text-red-700">
                    <span>{error}</span>
                    <button onClick={() => setError(null)}><X size={18} /></button>
                </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 divide-y divide-gray-100">
                {/* Profil Ayarları */}
                <div className="p-6">
                    <div className="flex items-center mb-4">
                        <div className="p-2 bg-blue-100 rounded-lg text-blue-600 mr-3">
                            <User size={20} />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900">{t('settings.profile_info')}</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.first_name')}</label>
                            <input
                                type="text"
                                value={profile.first_name}
                                onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.last_name')}</label>
                            <input
                                type="text"
                                value={profile.last_name}
                                onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.email')}</label>
                            <input
                                type="email"
                                value={profile.email}
                                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    <button
                        onClick={handleProfileSave}
                        className="mt-4 flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Save size={18} className="mr-2" />
                        {t('common.save')}
                    </button>
                </div>

                {/* Güvenlik */}
                <div className="p-6">
                    <div className="flex items-center mb-4">
                        <div className="p-2 bg-purple-100 rounded-lg text-purple-600 mr-3">
                            <Lock size={20} />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900">{t('settings.security')}</h2>
                    </div>
                    <div className="flex gap-4">
                        <button
                            onClick={() => setShowPasswordModal(true)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                            {t('settings.change_password')}
                        </button>
                        <button
                            onClick={requestMagicLink}
                            className="flex items-center text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                            <Mail size={16} className="mr-1" />
                            {t('settings.send_magic_link')}
                        </button>
                    </div>
                </div>

                {/* Bildirimler */}
                <div className="p-6">
                    <div className="flex items-center mb-4">
                        <div className="p-2 bg-yellow-100 rounded-lg text-yellow-600 mr-3">
                            <Bell size={20} />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900">{t('settings.notifications')}</h2>
                    </div>
                    
                    {userPrefs.organizations.length === 0 && userPrefs.customers.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">{t('settings.no_active_memberships', 'Aktif organizasyon veya müşteri üyeliğiniz bulunmuyor.')}</p>
                    ) : (
                        <div className="space-y-6">
                            {userPrefs.organizations.map(org => {
                                // Find customers belonging to this organization
                                const nestedCustomers = userPrefs.customers.filter(
                                    cust => cust.organization_id === org.organization_id
                                );
                                
                                return (
                                    <div key={org.membership_id} className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-4">
                                        {/* Organization Header & Toggles */}
                                        <div className="border-b border-gray-100 pb-3">
                                            <div className="font-bold text-gray-900 text-base mb-2">
                                                {org.organization_name} 
                                                <span className="text-xs font-normal text-gray-500 ml-2">({org.role})</span>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3 bg-blue-50/50 p-3 rounded-lg border border-blue-100/50">
                                                <label className="flex items-center space-x-2 text-sm font-medium text-blue-900 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={org.notify_on_start}
                                                        onChange={() => handlePrefToggle('organization', org.membership_id, 'notify_on_start', org.notify_on_start)}
                                                        className="rounded text-blue-600 focus:ring-blue-500"
                                                    />
                                                    <span>{t('settings.notify_on_start', 'Süreç Başladı')}</span>
                                                </label>
                                                <label className="flex items-center space-x-2 text-sm font-medium text-blue-900 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={org.notify_on_success}
                                                        onChange={() => handlePrefToggle('organization', org.membership_id, 'notify_on_success', org.notify_on_success)}
                                                        className="rounded text-blue-600 focus:ring-blue-500"
                                                    />
                                                    <span>{t('settings.notify_on_success', 'Süreç Başarılı')}</span>
                                                </label>
                                                <label className="flex items-center space-x-2 text-sm font-medium text-blue-900 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={org.notify_on_failure}
                                                        onChange={() => handlePrefToggle('organization', org.membership_id, 'notify_on_failure', org.notify_on_failure)}
                                                        className="rounded text-blue-600 focus:ring-blue-500"
                                                    />
                                                    <span>{t('settings.notify_on_failure', 'Süreç Hatalı')}</span>
                                                </label>
                                            </div>
                                            <p className="text-xs text-blue-700/80 mt-2 ml-1">
                                                💡 {t('settings.org_notification_hint', 'Bu seçeneği işaretlediğinizde, bu organizasyon altındaki tüm müşteri süreçlerinin bildirimleri size iletilir.')}
                                            </p>
                                        </div>

                                        {/* Nested Customers */}
                                        {nestedCustomers.length > 0 && (
                                            <div className="pl-4 border-l-2 border-gray-100 space-y-3">
                                                <div className="text-xs font-semibold text-gray-500 tracking-wider uppercase mb-1">
                                                    {t('settings.customer_notifications', 'Müşteri Bildirim Ayarları')}
                                                </div>
                                                {nestedCustomers.map(cust => (
                                                    <div key={cust.membership_id} className="bg-gray-50/50 p-3 rounded-lg border border-gray-100">
                                                        <div className="font-semibold text-gray-800 text-sm mb-2">
                                                            {cust.customer_name} 
                                                            <span className="text-xs font-normal text-gray-500 ml-2">({cust.role})</span>
                                                        </div>
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                            <label className="flex items-center space-x-2 text-xs text-gray-700 cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    disabled={org.notify_on_start}
                                                                    checked={org.notify_on_start || cust.notify_on_start}
                                                                    onChange={() => handlePrefToggle('customer', cust.membership_id, 'notify_on_start', cust.notify_on_start)}
                                                                    className="rounded text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                                                                />
                                                                <span className={org.notify_on_start ? 'text-gray-400 font-medium' : ''}>
                                                                    {t('settings.notify_on_start', 'Süreç Başladı')}
                                                                </span>
                                                            </label>
                                                            <label className="flex items-center space-x-2 text-xs text-gray-700 cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    disabled={org.notify_on_success}
                                                                    checked={org.notify_on_success || cust.notify_on_success}
                                                                    onChange={() => handlePrefToggle('customer', cust.membership_id, 'notify_on_success', cust.notify_on_success)}
                                                                    className="rounded text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                                                                />
                                                                <span className={org.notify_on_success ? 'text-gray-400 font-medium' : ''}>
                                                                    {t('settings.notify_on_success', 'Süreç Başarılı')}
                                                                </span>
                                                            </label>
                                                            <label className="flex items-center space-x-2 text-xs text-gray-700 cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    disabled={org.notify_on_failure}
                                                                    checked={org.notify_on_failure || cust.notify_on_failure}
                                                                    onChange={() => handlePrefToggle('customer', cust.membership_id, 'notify_on_failure', cust.notify_on_failure)}
                                                                    className="rounded text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                                                                />
                                                                <span className={org.notify_on_failure ? 'text-gray-400 font-medium' : ''}>
                                                                    {t('settings.notify_on_failure', 'Süreç Hatalı')}
                                                                </span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Independent Customers */}
                            {(() => {
                                const orgIds = userPrefs.organizations.map(o => o.organization_id);
                                const independentCustomers = userPrefs.customers.filter(
                                    cust => !orgIds.includes(cust.organization_id)
                                );
                                
                                if (independentCustomers.length === 0) return null;
                                
                                return (
                                    <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-4">
                                        <div className="border-b border-gray-100 pb-2">
                                            <h3 className="text-sm font-semibold text-gray-800 tracking-wider uppercase">
                                                {t('settings.independent_customers', 'Bağımsız Müşteri Bildirim Ayarları')}
                                            </h3>
                                        </div>
                                        <div className="space-y-3">
                                            {independentCustomers.map(cust => (
                                                <div key={cust.membership_id} className="bg-gray-50/50 p-3 rounded-lg border border-gray-100">
                                                    <div className="font-semibold text-gray-800 text-sm mb-2">
                                                        {cust.customer_name} 
                                                        <span className="text-xs font-normal text-gray-500 ml-2">({cust.role})</span>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                        <label className="flex items-center space-x-2 text-xs text-gray-700 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={cust.notify_on_start}
                                                                onChange={() => handlePrefToggle('customer', cust.membership_id, 'notify_on_start', cust.notify_on_start)}
                                                                className="rounded text-blue-600 focus:ring-blue-500"
                                                            />
                                                            <span>{t('settings.notify_on_start', 'Süreç Başladı')}</span>
                                                        </label>
                                                        <label className="flex items-center space-x-2 text-xs text-gray-700 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={cust.notify_on_success}
                                                                onChange={() => handlePrefToggle('customer', cust.membership_id, 'notify_on_success', cust.notify_on_success)}
                                                                className="rounded text-blue-600 focus:ring-blue-500"
                                                            />
                                                            <span>{t('settings.notify_on_success', 'Süreç Başarılı')}</span>
                                                        </label>
                                                        <label className="flex items-center space-x-2 text-xs text-gray-700 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={cust.notify_on_failure}
                                                                onChange={() => handlePrefToggle('customer', cust.membership_id, 'notify_on_failure', cust.notify_on_failure)}
                                                                className="rounded text-blue-600 focus:ring-blue-500"
                                                            />
                                                            <span>{t('settings.notify_on_failure', 'Süreç Hatalı')}</span>
                                                        </label>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>

                {/* Sistem */}
                <div className="p-6">
                    <div className="flex items-center mb-4">
                        <div className="p-2 bg-green-100 rounded-lg text-green-600 mr-3">
                            <Globe size={20} />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900">{t('settings.system')}</h2>
                    </div>
                    <div className="text-sm text-gray-500">
                        {t('settings.version')} v2.0.0 (FAZ 7 - Frontend Update)
                    </div>
                </div>
            </div>

            {/* Password Change Modal */}
            {showPasswordModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">{t('settings.change_password')}</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.old_password')}</label>
                                <input
                                    type="password"
                                    value={passwordForm.old_password}
                                    onChange={(e) => setPasswordForm({ ...passwordForm, old_password: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.new_password')}</label>
                                <input
                                    type="password"
                                    value={passwordForm.new_password}
                                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.confirm_password')}</label>
                                <input
                                    type="password"
                                    value={passwordForm.confirm_password}
                                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => setShowPasswordModal(false)}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handlePasswordChange}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                {t('settings.change_password')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Settings;
